/**
 * `<ChatComposer>` — the chat prompt line, rebuilt as a retro terminal entry (2026-05-23).
 *
 * A terminal-style prompt: a `|<` (Aletheia mark) prefix followed by an inline, auto-growing
 * entry. **Enter submits** (the terminal contract — type/paste a question, hit enter, it
 * outputs); **Shift+Enter** inserts a newline for multi-line input. A compact mono control
 * strip above the prompt carries the workflow-routing select + the Fast/Deep tool-tier toggle.
 *
 * It is **disabled while a turn is streaming** (`isStreaming`) — the chat is stateful within a
 * session and a turn must complete before the next starts (PRD §8.9; `useChatStream` also
 * guards a double-send, but disabling the entry is the visible signal).
 *
 * Per editor-chat.md §1.3 the chat keeps its *non-model affordances* working when the model is
 * missing — so `disabled` here is driven only by `isStreaming`; a `model_unavailable` turn
 * still lets the user re-issue. The surface owns that policy; this component just renders it.
 *
 * Pure presentational + local input state — the surface owns `sendTurn`.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useSystemStatusQuery } from '@/hooks';
import { useChatViewStore } from '@/stores';
import { TERMINAL_SAMPLE_QUERIES } from './terminalSampleQueries';

/** The named workflows the agent can be biased toward (backend catalogue). */
const COMPOSER_WORKFLOWS: { id: string; label: string }[] = [
  { id: '', label: 'auto' },
  { id: 'synthesize_topic', label: 'synthesize' },
  { id: 'find_relationships', label: 'relationships' },
  { id: 'trace_connections', label: 'connections' },
  { id: 'build_timeline', label: 'timeline' },
  { id: 'find_quotes', label: 'quotes' },
  { id: 'whats_on_my_plate', label: 'my plate' },
  { id: 'find_contradictions', label: 'contradictions' },
];

/** The terminal prompt prefix shown before the entry — the Aletheia mark, matching the
 *  scrollback's echoed prompt. */
export const TERMINAL_PROMPT = '|<';

/** Persisted CLI-style command history (survives reloads, like a shell's history file). */
const HISTORY_KEY = 'aletheia.chat.cmdHistory';
const HISTORY_CAP = 100;

function loadHistory(): string[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    if (raw === null) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function saveHistory(history: string[]): void {
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(history.slice(-HISTORY_CAP)));
  } catch {
    // localStorage unavailable / quota — history is a convenience, never load-bearing.
  }
}

/** `<ChatComposer>` props — the submit callback + the streaming-disable flag. */
export interface ChatComposerProps {
  /** Submit the composed message — the surface wires this to `useChatStream.sendTurn`.
   *  `opts.workflow` carries the composer's workflow-routing selection (null = auto). */
  onSend: (
    message: string,
    opts?: { workflow?: string | null; mode?: 'fast' | 'deep' },
  ) => void;
  /** True while a turn is streaming — disables the entry until the turn completes. */
  isStreaming: boolean;
}

/**
 * `<SampleQueryTicker>` — the idle "what can I ask?" helper: cycles white sample queries next
 * to the blinking cursor. Mounted only while the prompt is empty (the parent gates it), so it
 * unmounts — and stops cycling — on the first keystroke. Starts at a random query so each
 * session opens on a different suggestion; advances every few seconds with a quick fade.
 */
function SampleQueryTicker(): JSX.Element {
  const [idx, setIdx] = useState(() =>
    Math.floor(Math.random() * TERMINAL_SAMPLE_QUERIES.length),
  );
  useEffect(() => {
    const id = window.setInterval(() => {
      setIdx((i) => (i + 1) % TERMINAL_SAMPLE_QUERIES.length);
    }, 3400);
    return () => window.clearInterval(id);
  }, []);
  // `key` re-mounts the span on each change so the fade-in animation re-runs.
  return (
    <span key={idx} className="chat-term-ghost__q">
      {TERMINAL_SAMPLE_QUERIES[idx]}
    </span>
  );
}

/**
 * The terminal prompt line. Owns the draft text locally; clears it on submit. Submits on
 * Enter (Shift+Enter inserts a newline). The entry auto-grows with its content up to a cap.
 */
export function ChatComposer({ onSend, isStreaming }: ChatComposerProps): JSX.Element {
  // Draft + workflow + mode live in the sessionStorage-backed view store so the unsent text
  // and the routing selections survive navigating away from /chat and back (and a reload).
  const draft = useChatViewStore((s) => s.draft);
  const setDraft = useChatViewStore((s) => s.setDraft);
  const workflow = useChatViewStore((s) => s.workflow);
  const setWorkflow = useChatViewStore((s) => s.setWorkflow);
  const mode = useChatViewStore((s) => s.mode);
  const setMode = useChatViewStore((s) => s.setMode);
  // Real corpus counts for the prompt's status line — same source as the GlobalStatusBar pills.
  const status = useSystemStatusQuery().data;
  const taRef = useRef<HTMLTextAreaElement>(null);

  // CLI-style command history. `history` is oldest→newest; `histPos` is the recall cursor
  // (null = editing the live draft, not navigating). `stashRef` holds the in-progress draft
  // while navigating so Down past the newest entry restores it (like a shell).
  const [history, setHistory] = useState<string[]>(() => loadHistory());
  const [histPos, setHistPos] = useState<number | null>(null);
  const stashRef = useRef('');

  // Auto-grow the entry to fit its content (terminal lines wrap rather than scroll), capped so a
  // long paste doesn't swallow the scrollback.
  useEffect(() => {
    const ta = taRef.current;
    if (ta === null) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 200)}px`;
  }, [draft]);

  // Keep the prompt focused so the user can type the moment the chat tab is open — no click
  // required. Focuses on mount and again whenever a turn finishes (the entry is disabled while
  // streaming, which drops focus). `preventScroll` so grabbing focus doesn't jump the page.
  useEffect(() => {
    if (!isStreaming) taRef.current?.focus({ preventScroll: true });
  }, [isStreaming]);

  // Re-grab focus if it has drifted (e.g. after clicking a session in the rail or a citation)
  // the instant the user starts typing — so a stray keystroke always lands in the prompt rather
  // than being lost. Guarded so it never steals focus from another field, a shortcut, or while
  // streaming; only single printable characters redirect.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent): void {
      if (isStreaming) return;
      const ta = taRef.current;
      if (ta === null || document.activeElement === ta) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const active = document.activeElement as HTMLElement | null;
      const tag = active?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || active?.isContentEditable) {
        return;
      }
      if (e.key.length === 1) ta.focus({ preventScroll: true });
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isStreaming]);

  const submit = useCallback(() => {
    const trimmed = draft.trim();
    if (trimmed === '' || isStreaming) return;
    onSend(trimmed, { workflow: workflow === '' ? null : workflow, mode });
    setDraft('');
    // Append to history (skip consecutive duplicates) and reset the recall cursor.
    setHistory((h) => {
      const next = h[h.length - 1] === trimmed ? h : [...h, trimmed].slice(-HISTORY_CAP);
      saveHistory(next);
      return next;
    });
    setHistPos(null);
    stashRef.current = '';
  }, [draft, isStreaming, onSend, workflow, mode]);

  // Move the caret to the end after a programmatic recall (the controlled value updates next
  // frame, so read it then).
  const caretToEnd = useCallback(() => {
    requestAnimationFrame(() => {
      const ta = taRef.current;
      if (ta !== null) ta.setSelectionRange(ta.value.length, ta.value.length);
    });
  }, []);

  // Recall an OLDER entry (Up). Starts from the newest; stashes the live draft first.
  const recallPrev = useCallback(() => {
    if (history.length === 0) return;
    if (histPos === null) {
      stashRef.current = draft;
      const np = history.length - 1;
      setHistPos(np);
      setDraft(history[np]);
    } else if (histPos > 0) {
      const np = histPos - 1;
      setHistPos(np);
      setDraft(history[np]);
    }
    caretToEnd();
  }, [history, histPos, draft, caretToEnd]);

  // Recall a NEWER entry (Down); past the newest restores the stashed in-progress draft.
  const recallNext = useCallback(() => {
    if (histPos === null) return;
    if (histPos < history.length - 1) {
      const np = histPos + 1;
      setHistPos(np);
      setDraft(history[np]);
    } else {
      setHistPos(null);
      setDraft(stashRef.current);
    }
    caretToEnd();
  }, [history, histPos, caretToEnd]);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      const ta = event.currentTarget;
      const caret = ta.selectionStart ?? 0;
      // Up recalls older history — but only when the caret is in the FIRST line, so Up still
      // moves between lines in a multi-line draft.
      if (event.key === 'ArrowUp' && !ta.value.slice(0, caret).includes('\n')) {
        if (history.length > 0) {
          event.preventDefault();
          recallPrev();
        }
        return;
      }
      // Down recalls newer history — only when navigating AND the caret is in the LAST line.
      if (event.key === 'ArrowDown' && histPos !== null && !ta.value.slice(caret).includes('\n')) {
        event.preventDefault();
        recallNext();
        return;
      }
      // Enter submits (the terminal contract); Shift+Enter inserts a newline.
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        submit();
      }
    },
    [submit, history, histPos, recallPrev, recallNext],
  );

  // Typing leaves history-navigation (the edited text becomes the live draft again).
  const onDraftChange = useCallback((value: string) => {
    setDraft(value);
    setHistPos(null);
  }, []);

  // The idle "what can I ask?" helper (cursor + cycling sample queries) shows only on an empty,
  // non-streaming prompt — it disappears the moment the user types (draft becomes non-empty).
  const showHelper = draft === '' && !isStreaming;

  return (
    <form
      className="chat-term-form"
      data-testid="chat-composer"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      {/* Compact mono control strip: corpus status + workflow routing + Fast/Deep tier. */}
      <div className="chat-term-controls">
        {status !== undefined && (
          <span className="chat-term-controls__corpus">
            corpus loaded · {status.totalLiveNotes.toLocaleString()} notes
            {status.totalChunks !== undefined &&
              ` · ${status.totalChunks.toLocaleString()} chunks`}
          </span>
        )}
        <span className="chat-term-controls__spacer" />
        <label
          className="chat-term-ctl"
          title="Bias this turn toward one named retrieval workflow (timeline, quotes, contradictions, …) — auto lets the agent pick its own tools."
        >
          <span className="chat-term-ctl__lbl">--workflow</span>
          <select
            className="chat-term-ctl__select"
            value={workflow}
            onChange={(e) => setWorkflow(e.target.value)}
            disabled={isStreaming}
            aria-label="Workflow routing"
          >
            {COMPOSER_WORKFLOWS.map((w) => (
              <option key={w.id} value={w.id}>
                {w.label}
              </option>
            ))}
          </select>
        </label>
        <div
          className="chat-term-ctl chat-term-tier"
          role="radiogroup"
          aria-label="Tool depth"
          title="Tool tier for this turn — fast answers with the curated workflow tools (lower latency); deep opens the full tool surface (slower, more thorough)."
        >
          <span className="chat-term-ctl__lbl">--mode</span>
          <button
            type="button"
            role="radio"
            aria-checked={mode === 'fast'}
            className={'chat-term-tier__opt' + (mode === 'fast' ? ' chat-term-tier__opt--on' : '')}
            onClick={() => setMode('fast')}
            disabled={isStreaming}
          >
            fast
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={mode === 'deep'}
            className={'chat-term-tier__opt' + (mode === 'deep' ? ' chat-term-tier__opt--on' : '')}
            onClick={() => setMode('deep')}
            disabled={isStreaming}
          >
            deep
          </button>
        </div>
      </div>

      {/* The prompt line: prefix + inline auto-growing entry. When the entry is empty (and not
          streaming) an overlay shows the blinking cursor + a cycling sample-query helper; both
          vanish on the first keystroke (draft becomes non-empty). */}
      <div className={'chat-term-prompt' + (isStreaming ? ' chat-term-prompt--busy' : '')}>
        <span className="chat-term-prompt__user">{TERMINAL_PROMPT}</span>
        <div className="chat-term-entry-wrap">
          <textarea
            ref={taRef}
            className={'chat-term-entry' + (showHelper ? ' chat-term-entry--idle' : '')}
            value={draft}
            onChange={(e) => onDraftChange(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={isStreaming ? 'awaiting response…' : ''}
            disabled={isStreaming}
            rows={1}
            spellCheck={false}
            autoComplete="off"
            aria-label="Chat message"
          />
          {showHelper && (
            <div className="chat-term-ghost" aria-hidden="true">
              <span className="chat-term-cursor" />
              <SampleQueryTicker />
            </div>
          )}
        </div>
      </div>
    </form>
  );
}
